/**
 * @file React error boundary with client log + suppressed camera/motion noise.
 */

import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { logClientError } from '../api/logError';

function isHarmlessVideoError(error) {
  const errorMessage = error?.message || '';
  const errorStack = error?.stack || '';
  const combinedMessage = (errorMessage + ' ' + errorStack).toLowerCase();

  return (
    errorMessage.includes('play()') ||
    errorMessage.includes('interrupted') ||
    errorMessage.includes('removed from the document') ||
    errorMessage.includes('The play() request was interrupted') ||
    errorMessage.includes('onabort') ||
    errorMessage.includes('onabort()') ||
    errorMessage.includes('video surface') ||
    errorMessage.includes('RenderedCameraImpl') ||
    errorMessage.includes('Rendered CameraImpl') ||
    combinedMessage.includes('onabort') ||
    combinedMessage.includes('renderedcameraimpl') ||
    combinedMessage.includes('rendered cameraimpl') ||
    combinedMessage.includes('video surface') ||
    combinedMessage.includes('cameraimpl video surface') ||
    (errorMessage.includes('rendered') && errorMessage.includes('cameraimpl'))
  );
}

/**
 * Motion (Framer) teardown noise only — do not treat every null-property
 * TypeError as harmless (that left blank screens with no recovery UI).
 */
function isHarmlessMotionError(error) {
  const errorMessage = error?.message || '';
  const errorStack = error?.stack || '';
  const combinedMessage = (errorMessage + ' ' + errorStack).toLowerCase();

  const hasMotionStack =
    combinedMessage.includes('resolveelements') ||
    combinedMessage.includes('resolvesubjects') ||
    combinedMessage.includes('animatesubject') ||
    combinedMessage.includes('scopedanimate') ||
    combinedMessage.includes('framer-motion') ||
    combinedMessage.includes('motion/react');

  const hasQuerySelectorAllNoise =
    combinedMessage.includes('queryselectorall') ||
    errorMessage.includes('querySelectorAll');

  return hasMotionStack || hasQuerySelectorAllNoise;
}

function shouldSuppressError(error) {
  return isHarmlessVideoError(error) || isHarmlessMotionError(error);
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    if (shouldSuppressError(error)) {
      console.log(
        '⚠️ Harmless error in ErrorBoundary.getDerivedStateFromError (suppressed):',
        error?.message || ''
      );
      return null;
    }

    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    if (shouldSuppressError(error)) {
      console.log(
        '⚠️ Harmless error in ErrorBoundary.componentDidCatch (suppressed):',
        error?.message || ''
      );
      if (error) {
        console.log('   Full error:', error);
      }
      return;
    }

    console.error('Error caught by boundary:', error, errorInfo);
    logClientError({
      message: error?.message || String(error),
      componentStack: errorInfo?.componentStack || '',
    });
    this.setState({
      error,
      errorInfo,
    });
  }

  render() {
    if (this.state.hasError && this.state.error && shouldSuppressError(this.state.error)) {
      console.log(
        '⚠️ Harmless error in ErrorBoundary.render (no overlay, console only):',
        this.state.error
      );
      return this.props.children;
    }

    if (this.state.hasError) {
      if (this.state.error) {
        console.error(
          '❌ Error caught by ErrorBoundary (check console for details):',
          this.state.error,
          this.state.errorInfo
        );
      }

      return (
        <div className="container" style={{ padding: '40px 20px' }}>
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              <AlertTriangle size={24} color="#dc3545" aria-hidden />
              <h2 style={{ margin: 0, color: '#dc3545' }}>Something went wrong</h2>
            </div>
            <p style={{ marginBottom: '20px' }}>
              An unexpected error occurred. Please try refreshing the page.
            </p>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '10px' }}>
                  Error Details (Development Only)
                </summary>
                <pre
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '12px',
                    color: '#666',
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
            <button
              onClick={() => window.location.reload()}
              className="btn btn-primary"
              style={{ marginTop: '20px' }}
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
